/**
 * Streaming Audio Uploader with IndexedDB Backup
 * 
 * Uploads audio chunks in real-time during recording to minimize
 * upload time after recording stops. Provides resilience against
 * browser close/screen off by persisting chunks to IndexedDB.
 */

import { getCookie } from '../utils/token';
import i18n from '../i18n';

/**
 * Get authorization headers for API requests.
 */
function getAuthHeaders(): HeadersInit {
  const accessToken = getCookie('accessToken');
  const headers: HeadersInit = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  // Add language header for server-side localization (same as getDefaultHeaders)
  const currentLanguage = i18n?.language || localStorage.getItem("i18nextLng") || "he";
  headers['Accept-Language'] = currentLanguage;
  // Required for localhost JWT audience validation (must match getDefaultHeaders)
  headers['X-App-Name'] = 'therapist';
  return headers;
}

// Cache for streaming availability check
let streamingAvailableCache: boolean | null = null;
let streamingCheckPromise: Promise<boolean> | null = null;

/**
 * Check if the streaming upload endpoint is available on the server.
 * Result is cached for the session.
 */
export async function isStreamingAvailable(apiBaseUrl: string): Promise<boolean> {
  // Return cached result if available
  if (streamingAvailableCache !== null) {
    return streamingAvailableCache;
  }

  // Return pending promise if already checking
  if (streamingCheckPromise) {
    return streamingCheckPromise;
  }

  // Perform the check using dedicated health-check endpoint
  streamingCheckPromise = (async () => {
    try {
      const testUrl = `${apiBaseUrl}/MeetingRecords/streamingAvailable`;
      const response = await fetch(testUrl, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      streamingAvailableCache = response.ok;
      // console.log(`[StreamingUploader] Streaming ${streamingAvailableCache ? 'available' : 'not available'}`);
      return streamingAvailableCache;
    } catch (error) {
      // Network error — DON'T cache false so we retry when online.
      // THROW so the caller can distinguish "server said no" from "can't reach server".
      // console.log('[StreamingUploader] Streaming check failed (network error) — will retry');
      streamingCheckPromise = null; // allow retry
      throw new Error('Network error checking streaming availability');
    }
  })();

  return streamingCheckPromise;
}

/**
 * Reset the streaming availability cache (useful for testing or after deployment)
 */
export function resetStreamingAvailabilityCache(): void {
  streamingAvailableCache = null;
  streamingCheckPromise = null;
}

const DB_NAME = 'RecordingChunksDB';
const DB_VERSION = 1;
const STORE_NAME = 'chunks';

export interface ChunkRecord {
  id: string; // `${sessionId}_${chunkIndex}`
  sessionId: string;
  chunkIndex: number;
  data: Blob;
  uploaded: boolean;
  createdAt: number;
}

export interface StreamingUploaderConfig {
  apiBaseUrl: string;
  userId: string;
  patientName?: string;
  patientGuid?: string;
  /** Optional appointment ID to link this recording to a calendar appointment */
  appointmentId?: number;
  /** Resume an existing session instead of creating a new one */
  resumeSessionId?: string;
  /** Chunk index to resume from (so new chunks continue the sequence) */
  resumeChunkIndex?: number;
  /** Dual-track call: therapist | patient */
  track?: 'therapist' | 'patient';
  /** Correlates both tracks for one video call */
  sharedCallId?: string;
  onProgress?: (uploaded: number, total: number) => void;
  onError?: (error: Error, chunkIndex: number) => void;
  onChunkUploaded?: (chunkIndex: number) => void;
  onConnectionQuality?: (quality: ConnectionQuality) => void;
}

export interface UploadSession {
  sessionId: string;
  userId: string;
  startedAt: number;
  chunkCount: number;
  uploadedCount: number;
  finalized: boolean;
}

export type ConnectionQuality = 'good' | 'slow' | 'poor' | 'offline';

export interface ConnectionStats {
  quality: ConnectionQuality;
  avgUploadTimeMs: number;
  pendingChunks: number;
  lastUploadTimeMs: number;
}

/**
 * Opens/creates the IndexedDB database
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('sessionId', 'sessionId', { unique: false });
        store.createIndex('uploaded', 'uploaded', { unique: false });
      }
    };
  });
}

/**
 * Saves a chunk to IndexedDB
 */
async function saveChunkToIndexedDB(chunk: ChunkRecord): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(chunk);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
    
    tx.oncomplete = () => db.close();
  });
}

/**
 * Marks a chunk as uploaded in IndexedDB
 */
async function markChunkUploaded(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getRequest = store.get(id);
    
    getRequest.onsuccess = () => {
      const chunk = getRequest.result;
      if (chunk) {
        chunk.uploaded = true;
        store.put(chunk);
      }
      resolve();
    };
    
    getRequest.onerror = () => reject(getRequest.error);
    tx.oncomplete = () => db.close();
  });
}

/**
 * Deletes all chunks for a session from IndexedDB
 */
async function deleteSessionChunks(sessionId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('sessionId');
    const request = index.openCursor(IDBKeyRange.only(sessionId));
    
    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
  });
}

/**
 * Gets all pending (not uploaded) chunks for a session
 */
async function getPendingChunks(sessionId: string): Promise<ChunkRecord[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('sessionId');
    const request = index.getAll(IDBKeyRange.only(sessionId));
    
    request.onsuccess = () => {
      const chunks = request.result.filter((c: ChunkRecord) => !c.uploaded);
      resolve(chunks.sort((a, b) => a.chunkIndex - b.chunkIndex));
    };
    
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}

/**
 * Gets all incomplete sessions from IndexedDB
 */
export async function getIncompleteSessions(): Promise<string[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    
    request.onsuccess = () => {
      const chunks = request.result as ChunkRecord[];
      const sessionIds = [...new Set(chunks.map(c => c.sessionId))];
      resolve(sessionIds);
    };
    
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}

/**
 * Clears all chunks from IndexedDB. Used when switching companies
 * to prevent chunk data from leaking across user identities.
 */
export async function clearAllLocalChunks(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.clear();
    
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

/**
 * StreamingUploader class - handles real-time chunk uploads during recording
 */
export class StreamingUploader {
  private sessionId: string;
  private config: StreamingUploaderConfig;
  private chunkIndex: number = 0;
  private uploadedCount: number = 0;
  private uploadQueue: Promise<void> = Promise.resolve();
  private isActive: boolean = true;
  private totalChunks: number = 0;
  private mimeType: string = 'audio/webm';
  
  // Connection quality tracking
  private uploadTimes: number[] = [];
  private lastQuality: ConnectionQuality = 'good';
  private failedUploads: number = 0;
  private isOnline: boolean = navigator.onLine;

  // Thresholds for connection quality (in milliseconds)
  private static readonly GOOD_THRESHOLD_MS = 2000;   // < 2s = good
  private static readonly SLOW_THRESHOLD_MS = 5000;   // < 5s = slow
  private static readonly POOR_THRESHOLD_MS = 10000;  // < 10s = poor, >= 10s or failures = offline/critical
  private static readonly RETRY_INTERVAL_MS = 10_000;  // retry pending chunks every 10s
  private static readonly UPLOAD_TIMEOUT_MS = 15_000;  // abort fetch after 15s to avoid indefinite hangs
  private durationSeconds: number = 0;
  private retryTimer: ReturnType<typeof setInterval> | null = null;
  private retryQueued: boolean = false;
  private currentAbortController: AbortController | null = null;
  private flushed = false;

  constructor(config: StreamingUploaderConfig) {
    this.config = config;
    // Resume existing session or generate a new one
    this.sessionId = config.resumeSessionId || this.generateSessionId();
    if (config.resumeChunkIndex != null) {
      this.chunkIndex = config.resumeChunkIndex;
      this.uploadedCount = config.resumeChunkIndex;
      this.totalChunks = config.resumeChunkIndex;
    }
    
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);

    // Periodic retry: every 10s, check for pending chunks and re-upload.
    // This catches cases where connectivity degrades/recovers without firing
    // browser online/offline events (e.g. DevTools throttling, flaky Wi-Fi).
    this.retryTimer = setInterval(() => this.retryPendingIfNeeded(), StreamingUploader.RETRY_INTERVAL_MS);
  }

  private handleOnline = () => {
    this.isOnline = true;
    this.failedUploads = 0;

    // Abort any in-flight fetch that's hanging from the offline period.
    if (this.currentAbortController) {
      // console.log('[StreamingUploader] 🔌 Online — aborting stale in-flight upload');
      this.currentAbortController.abort();
    }

    // RESET the upload queue — any queued offline fetches are useless.
    // We'll re-upload everything from IndexedDB in the right order.
    this.uploadQueue = Promise.resolve();

    this.updateConnectionQuality();

    // Immediately upload pending chunks from IndexedDB
    if (this.isActive) {
      this.uploadQueue = this.uploadQueue.then(async () => {
        try {
          await this.uploadPendingChunks();
          this.updateConnectionQuality();
          // console.log('[StreamingUploader] ✅ Pending chunks uploaded after reconnect');
        } catch (err) {
          // console.warn('[StreamingUploader] ⚠️ Some pending chunks still failed after reconnect:', err);
        }
      });
    }
  };

  private handleOffline = () => {
    this.isOnline = false;
    this.lastQuality = 'offline';
    this.config.onConnectionQuality?.('offline');

    // Abort any in-flight fetch so the queue doesn't hang for minutes
    if (this.currentAbortController) {
      // console.log('[StreamingUploader] 📴 Offline — aborting in-flight upload');
      this.currentAbortController.abort();
    }
  };

  private generateSessionId(): string {
    return `${this.config.userId}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Periodic retry of pending chunks. Runs on a timer so it works even when
   * browser online/offline events don't fire (e.g. DevTools throttling).
   * Runs INDEPENDENTLY of the upload queue — the queue may be clogged with
   * timing-out fetches, so we can't append to it.
   */
  private retryPendingIfNeeded = async () => {
    // Guard: skip if inactive, already retrying, or nothing pending
    if (!this.isActive) return;
    if (this.retryQueued) return;
    if (this.uploadedCount >= this.totalChunks && this.failedUploads === 0) return;

    // Quick connectivity check — if we're offline, don't bother
    if (!navigator.onLine) return;

    // console.log(`[StreamingUploader] ⏱️ Retry timer fired (uploaded=${this.uploadedCount}/${this.totalChunks}, failed=${this.failedUploads})`);

    this.retryQueued = true;
    try {
      const pending = await getPendingChunks(this.sessionId);
      if (pending.length === 0) {
        // console.log('[StreamingUploader] ⏱️ No pending chunks to retry');
        return;
      }

      // Reset the queue — any stale fetches queued while offline are useless
      this.uploadQueue = Promise.resolve();
      this.failedUploads = 0;

      // console.log(`[StreamingUploader] 🔄 Retrying ${pending.length} pending chunks...`);
      for (const chunk of pending) {
        try {
          await this.uploadChunk(chunk.chunkIndex, chunk.data);
          await markChunkUploaded(chunk.id);
          this.uploadedCount++;
          this.config.onProgress?.(this.uploadedCount, this.totalChunks);
          // console.log(`[StreamingUploader] ✅ Retry: chunk ${chunk.chunkIndex} uploaded (${this.uploadedCount}/${this.totalChunks})`);
        } catch (err) {
          // console.warn(`[StreamingUploader] ⚠️ Retry failed for chunk ${chunk.chunkIndex}:`, err);
          break; // stop this cycle, next interval will try again
        }
      }
      this.updateConnectionQuality();
    } catch (err) {
      // console.warn('[StreamingUploader] ⚠️ Retry: IndexedDB read failed:', err);
    } finally {
      this.retryQueued = false;
    }
  };

  /**
   * Get the current session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Set the MIME type for the recording
   */
  setMimeType(mimeType: string): void {
    this.mimeType = mimeType;
  }

  /**
   * Update the recording duration (called periodically from the recorder)
   */
  setDurationSeconds(seconds: number): void {
    this.durationSeconds = seconds;
  }

  /**
   * Add a chunk to be uploaded. Called from MediaRecorder.ondataavailable
   */
  async addChunk(blob: Blob): Promise<void> {
    if (!this.isActive) return;

    const chunkIndex = this.chunkIndex++;
    this.totalChunks++;
    const id = `${this.sessionId}_${chunkIndex}`;

    // Save to IndexedDB first (backup)
    const chunkRecord: ChunkRecord = {
      id,
      sessionId: this.sessionId,
      chunkIndex,
      data: blob,
      uploaded: false,
      createdAt: Date.now(),
    };

    await saveChunkToIndexedDB(chunkRecord);

    // If offline or recent failures, DON'T queue a fetch — just save to IndexedDB.
    // The retry timer / handleOnline will pick up pending chunks later.
    // This prevents the upload queue from clogging with 15s-timeout fetches.
    if (!navigator.onLine || !this.isOnline || this.failedUploads >= 2) {
      // console.log(`[StreamingUploader] 💾 Chunk ${chunkIndex} saved to IndexedDB (offline/failing, skipping upload)`);
      this.config.onProgress?.(this.uploadedCount, this.totalChunks);
      return;
    }

    // Queue upload (sequential to maintain order, but non-blocking)
    this.uploadQueue = this.uploadQueue.then(async () => {
      try {
        await this.uploadChunk(chunkIndex, blob);
        await markChunkUploaded(id);
        this.uploadedCount++;
        // console.log(`[StreamingUploader] ✅ Chunk ${chunkIndex} uploaded (session=${this.sessionId}, duration=${this.durationSeconds}s, total=${this.totalChunks})`);
        
        this.config.onChunkUploaded?.(chunkIndex);
        this.config.onProgress?.(this.uploadedCount, this.totalChunks);
      } catch (error) {
        // console.error(`[StreamingUploader] ❌ Failed to upload chunk ${chunkIndex} (session=${this.sessionId}):`, error);
        this.config.onError?.(error as Error, chunkIndex);
        // Chunk stays in IndexedDB for retry
      }
    });
  }

  /**
   * Upload a single chunk to the server
   */
  private async uploadChunk(chunkIndex: number, blob: Blob): Promise<void> {
    const formData = new FormData();
    formData.append('chunk', blob, `chunk_${chunkIndex}.bin`);
    formData.append('mimeType', this.mimeType);
    // Send patient metadata for server-side session tracking (recovery UI)
    if (this.config.patientName) formData.append('patientName', this.config.patientName);
    if (this.config.patientGuid) formData.append('patientGuid', this.config.patientGuid);
    if (this.durationSeconds > 0) formData.append('durationSeconds', String(this.durationSeconds));
    if (this.config.appointmentId) formData.append('appointmentId', String(this.config.appointmentId));
    if (this.config.track) formData.append('track', this.config.track);
    if (this.config.sharedCallId) formData.append('sharedCallId', this.config.sharedCallId);

    const url = `${this.config.apiBaseUrl}/MeetingRecords/uploadChunk/${this.sessionId}/${chunkIndex}`;
    
    const startTime = Date.now();
    const controller = new AbortController();
    this.currentAbortController = controller;

    // Auto-abort after timeout so we never hang indefinitely when offline
    const timeoutId = setTimeout(() => controller.abort(), StreamingUploader.UPLOAD_TIMEOUT_MS);
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const uploadTime = Date.now() - startTime;
      this.recordUploadTime(uploadTime, true);

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      const uploadTime = Date.now() - startTime;
      this.recordUploadTime(uploadTime, false);
      throw error;
    } finally {
      if (this.currentAbortController === controller) {
        this.currentAbortController = null;
      }
    }
  }

  /**
   * Record upload time and update connection quality
   */
  private recordUploadTime(timeMs: number, success: boolean): void {
    if (success) {
      this.uploadTimes.push(timeMs);
      // Keep only last 5 upload times for averaging
      if (this.uploadTimes.length > 5) {
        this.uploadTimes.shift();
      }
      this.failedUploads = 0;
    } else {
      this.failedUploads++;
    }
    
    this.updateConnectionQuality();
  }

  /**
   * Calculate and report connection quality based on upload times
   */
  private updateConnectionQuality(): void {
    if (!this.isOnline) {
      if (this.lastQuality !== 'offline') {
        this.lastQuality = 'offline';
        this.config.onConnectionQuality?.('offline');
      }
      return;
    }

    let quality: ConnectionQuality;
    
    // If we have recent failures, connection is poor
    if (this.failedUploads >= 2) {
      quality = 'poor';
    } else if (this.uploadTimes.length === 0) {
      quality = 'good'; // No data yet, assume good
    } else {
      // Calculate average upload time
      const avgTime = this.uploadTimes.reduce((a, b) => a + b, 0) / this.uploadTimes.length;
      
      if (avgTime < StreamingUploader.GOOD_THRESHOLD_MS) {
        quality = 'good';
      } else if (avgTime < StreamingUploader.SLOW_THRESHOLD_MS) {
        quality = 'slow';
      } else {
        quality = 'poor';
      }
    }

    // Also factor in pending chunks - if falling behind, connection is slow
    const pendingChunks = this.totalChunks - this.uploadedCount;
    if (pendingChunks >= 5 && quality === 'good') {
      quality = 'slow';
    } else if (pendingChunks >= 10) {
      quality = 'poor';
    }

    if (quality !== this.lastQuality) {
      this.lastQuality = quality;
      this.config.onConnectionQuality?.(quality);
    }
  }

  /**
   * Get current connection statistics
   */
  getConnectionStats(): ConnectionStats {
    const avgTime = this.uploadTimes.length > 0
      ? this.uploadTimes.reduce((a, b) => a + b, 0) / this.uploadTimes.length
      : 0;
    
    return {
      quality: this.lastQuality,
      avgUploadTimeMs: Math.round(avgTime),
      pendingChunks: this.totalChunks - this.uploadedCount,
      lastUploadTimeMs: this.uploadTimes[this.uploadTimes.length - 1] || 0,
    };
  }

  /**
   * Upload any pending chunks that failed previously
   */
  async uploadPendingChunks(): Promise<void> {
    const pending = await getPendingChunks(this.sessionId);
    
    for (const chunk of pending) {
      try {
        await this.uploadChunk(chunk.chunkIndex, chunk.data);
        await markChunkUploaded(chunk.id);
        this.uploadedCount++;
        this.config.onProgress?.(this.uploadedCount, this.totalChunks);
      } catch (error) {
        // console.error(`[StreamingUploader] Failed to upload pending chunk ${chunk.chunkIndex}:`, error);
        throw error;
      }
    }
  }

  /**
   * Get the current upload status
   */
  getUploadStatus(): { uploaded: number; total: number; pending: number } {
    return {
      uploaded: this.uploadedCount,
      total: this.totalChunks,
      pending: this.totalChunks - this.uploadedCount,
    };
  }

  /**
   * Wait for all queued uploads to complete with progress callback
   */
  async waitForUploadsWithProgress(onProgress?: (uploaded: number, total: number) => void): Promise<void> {
    // If there's nothing pending, return immediately
    if (this.uploadedCount >= this.totalChunks) {
      onProgress?.(this.totalChunks, this.totalChunks);
      return;
    }

    // Poll for progress while waiting
    const pollInterval = setInterval(() => {
      onProgress?.(this.uploadedCount, this.totalChunks);
    }, 500);

    try {
      await this.uploadQueue;
      // Final progress update
      onProgress?.(this.uploadedCount, this.totalChunks);
    } finally {
      clearInterval(pollInterval);
    }
  }

  /**
   * Wait for all queued uploads to complete
   */
  async waitForUploads(): Promise<void> {
    await this.uploadQueue;
  }

  /**
   * Finalize the upload session - tells server to assemble chunks
   */
  async finalize(): Promise<{
    success: boolean;
    recordNumber?: number;
    questionnaireCompaniesScoreUsersId?: number | null;
    cycle?: number | null;
    error?: string;
  }> {
    this.isActive = false;

    // Wait for all uploads to complete
    await this.uploadQueue;

    // Upload any remaining pending chunks
    await this.uploadPendingChunks();

    // Call finalize endpoint
    const url = `${this.config.apiBaseUrl}/MeetingRecords/finalizeStreamingUpload/${this.sessionId}`;
    
    const authHeaders = getAuthHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        usersGuid: this.config.patientGuid,
        totalChunks: this.totalChunks,
        mimeType: this.mimeType,
        appointmentId: this.config.appointmentId || null,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      return { success: false, error: text };
    }

    const result = await response.json();

    // Clean up IndexedDB on success
    if (result.success) {
      await deleteSessionChunks(this.sessionId);
    }

    return result;
  }

  /**
   * Drain the upload queue without calling finalizeStreamingUpload.
   * IndexedDB chunks are preserved until purgeStoredChunks() after server finalize succeeds.
   */
  async flushAndStop(): Promise<void> {
    if (this.flushed) return;
    this.flushed = true;
    this.isActive = false;
    if (this.retryTimer) {
      clearInterval(this.retryTimer);
      this.retryTimer = null;
    }
    await this.uploadQueue;
    try {
      await this.uploadPendingChunks();
    } catch {
      // Best-effort drain; remaining chunks stay in IndexedDB for retry
    }
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  /**
   * Remove local IndexedDB backup for this session (after successful server finalize).
   */
  async purgeStoredChunks(): Promise<void> {
    await deleteSessionChunks(this.sessionId);
  }

  /**
   * Cancel the upload session
   */
  async cancel(): Promise<void> {
    this.isActive = false;
    
    // Remove event listeners
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    
    await deleteSessionChunks(this.sessionId);
    
    // Optionally notify server to clean up
    try {
      await fetch(`${this.config.apiBaseUrl}/MeetingRecords/cancelStreamingUpload/${this.sessionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
    } catch {
      // Ignore errors during cleanup
    }
  }

  /**
   * Cleanup resources (call when done with uploader)
   */
  cleanup(): void {
    if (this.retryTimer) {
      clearInterval(this.retryTimer);
      this.retryTimer = null;
    }
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  /**
   * Get upload statistics
   */
  getStats(): { uploaded: number; total: number; pending: number } {
    return {
      uploaded: this.uploadedCount,
      total: this.totalChunks,
      pending: this.totalChunks - this.uploadedCount,
    };
  }
}

/**
 * Resume an incomplete session from IndexedDB
 */
export async function resumeSession(
  sessionId: string, 
  config: Omit<StreamingUploaderConfig, 'userId'>
): Promise<StreamingUploader | null> {
  const pending = await getPendingChunks(sessionId);
  if (pending.length === 0) {
    await deleteSessionChunks(sessionId);
    return null;
  }

  // Extract userId from sessionId (format: userId_timestamp_random)
  const userId = sessionId.split('_')[0];
  
  const uploader = new StreamingUploader({ ...config, userId });
  // Override sessionId to resume existing session
  (uploader as any).sessionId = sessionId;
  (uploader as any).totalChunks = pending.length;
  
  return uploader;
}
